import {SelectedDrawingVersions} from "../../features/drawing-exporter";
import {useTranslation} from "react-i18next";
import {useProjectDetailContext} from "../../providers/ProjectDetailProvider";
import {useDrawingVersionsSummaryExporter} from "../../features/sync-materials/hooks/useDrawingVersionsSummaryExporter";
import {useSiteAttachmentExporter} from "../../features/sync-materials/hooks/useSiteAttachmentExporter";
import React, {useState} from "react";
import {INITIAL_VERSION_ID} from "../../../project-drawings/features/editor/constants";
import {ProjectSource, Status} from "../../../../models/enums";
import NotificationService from "../../../../services/NotificationService";
import LoggerService from "../../../../services/LoggerService";
import Button from "../../../../components/ui/Button";
import {SendAttachmentsConfirmationModal} from "./SendAttachmentsConfirmationModal";

export function SendSelectedAttachmentsButton({selection}: { selection: SelectedDrawingVersions }) {

	const {t} = useTranslation();
	const {projectDetails, treeData: {sections, selectionData}, fitTimesData} = useProjectDetailContext()
	const drawingVersionsSummaryExporter = useDrawingVersionsSummaryExporter()
	const siteAttachmentExporter = useSiteAttachmentExporter()
	const {fitTimes, loadStatus: fitTimesLoadStatus} = fitTimesData
	const [projectConfirmModalOpen, setProjectConfirmModalOpen] = useState(false);
	const [siteConfirmModalOpen, setSiteConfirmModalOpen] = useState(false);

	const selectedDrawingDataList = React.useMemo(() => {
		return selection.filter(item => (
			item.selectedVersionId !== INITIAL_VERSION_ID
		)).map(item => ({
			versionId: item.selectedVersionId,
			drawingType: item.drawingType
		}))
	}, [selection])

	const baseCanSend = Boolean(
		fitTimesLoadStatus === Status.SUCCESS &&
		!!projectDetails.project &&
		projectDetails.project.id &&
		selectedDrawingDataList.length > 0
	);

	const canSendSelectedToSimpro = Boolean(
		baseCanSend &&
		drawingVersionsSummaryExporter.status !== Status.LOADING
	);

	const canSendSelectedToSite = Boolean(
		baseCanSend &&
		siteAttachmentExporter.status !== Status.LOADING &&
		!!projectDetails.project?.siteId
	);

	function handleSendSelectedToSimpro() {
		if (!canSendSelectedToSimpro) {
			return;
		}

		drawingVersionsSummaryExporter.exportDrawingVersionsSummary(
			projectDetails.project!.id,
			selectedDrawingDataList,
			fitTimes,
			sections,
			() => {
				setProjectConfirmModalOpen(false);
				selectionData.reset();
				NotificationService.successNotification(
					t("common.success"),
					t("projects.details.sendAttachments.sendSuccessDesc")
				);
			},
			error => {
				LoggerService.logError(error);
				NotificationService.errorNotification(
					t("common.error"),
					t("projects.details.sendAttachments.sendErrorDesc")
				);
			}
		)
	}

	function handleSendSelectedToSite() {
		if (!canSendSelectedToSite) {
			return;
		}

		siteAttachmentExporter.exportToSite(
			projectDetails.project!.siteId!,
			projectDetails.project!.source,
			projectDetails.project!.number,
			selectedDrawingDataList,
			fitTimes,
			sections,
			() => {
				setSiteConfirmModalOpen(false);
				selectionData.reset();
				NotificationService.successNotification(
					t("common.success"),
					t("projects.details.sendAttachments.site.sendSuccessDesc")
				);
			},
			error => {
				LoggerService.logError(error);
				NotificationService.errorNotification(
					t("common.error"),
					t("projects.details.sendAttachments.site.sendErrorDesc")
				);
			}
		)
	}

	const projectConfirmButtonLabel = React.useMemo(() => {
		if (drawingVersionsSummaryExporter.status === Status.LOADING) {
			return t("projects.details.sendAttachments.confirmationModal.processingButton")
		}
		return t("projects.details.sendAttachments.confirmationModal.confirmButton")
	}, [drawingVersionsSummaryExporter.status, t])

	const sendToProjectButtonLabel = React.useMemo(() => {
		return projectDetails.project?.source === ProjectSource.JOB
			? t("projects.details.sendAttachments.job.button")
			: t("projects.details.sendAttachments.quote.button")
	}, [projectDetails.project?.source, t])

	const siteConfirmButtonLabel = React.useMemo(() => {
		if (siteAttachmentExporter.status === Status.LOADING) {
			return t("projects.details.sendAttachments.site.confirmationModal.processingButton")
		}
		return t("projects.details.sendAttachments.site.confirmationModal.confirmButton")
	}, [siteAttachmentExporter.status, t])

	return (
		<>
			<SendAttachmentsConfirmationModal
				isOpen={projectConfirmModalOpen}
				isLoading={drawingVersionsSummaryExporter.status === Status.LOADING}
				title={t("projects.details.sendAttachments.confirmationModal.title")}
				message={t("projects.details.sendAttachments.confirmationModal.message")}
				confirmButton={projectConfirmButtonLabel}
				processingButton={t("projects.details.sendAttachments.confirmationModal.processingButton")}
				onConfirm={handleSendSelectedToSimpro}
				onClose={() => setProjectConfirmModalOpen(false)}
			/>
			<SendAttachmentsConfirmationModal
				isOpen={siteConfirmModalOpen}
				isLoading={siteAttachmentExporter.status === Status.LOADING}
				title={t("projects.details.sendAttachments.site.confirmationModal.title")}
				message={t("projects.details.sendAttachments.site.confirmationModal.message")}
				confirmButton={siteConfirmButtonLabel}
				processingButton={t("projects.details.sendAttachments.site.confirmationModal.processingButton")}
				onConfirm={handleSendSelectedToSite}
				onClose={() => setSiteConfirmModalOpen(false)}
			/>
			<Button
				label={sendToProjectButtonLabel}
				variant={"secondary"}
				disabled={!canSendSelectedToSimpro}
				onClick={() => { setProjectConfirmModalOpen(true); }}
			/>
			{!!projectDetails.project?.siteId && (
				<Button
					label={t("projects.details.sendAttachments.site.button")}
					variant={"secondary"}
					disabled={!canSendSelectedToSite}
					onClick={() => { setSiteConfirmModalOpen(true); }}
				/>
			)}
		</>
	)
}
